import { useMemo, useState, useEffect, useRef } from 'react'
import { HelpCircle, ChevronUp, ChevronDown, Plus, Trash2, AlertTriangle } from 'lucide-react'
import { Tooltip } from '../../../components/ui/Tooltip'
import { useScopeStore } from '../store'
import { useHasResellerParent, ResellerInheritedBanner } from './ResellerLock'
import { getGuidance } from '../guidance'
import { getFieldById, getGroupById, getSectionById } from '../schema'
import { useUpsellContext } from '../hooks/useUpsellContext'
import {
  AU_DOCUMENT_TYPES,
  AU_OTHER_DOCUMENT_TYPES,
  COMMON_COUNTRIES,
  KYC_RULESETS,
  V1_KYC_RULESETS,
  WORKFLOW_CASCADE_SOURCES,
  type TriState,
  type WorkflowCascadeSource,
} from '../types'

function RequiredMark() {
  return <span className="text-blue-400 ml-1">*</span>
}

function SectionHeader({
  title,
  subtitle,
}: {
  title: string
  subtitle?: string
}) {
  return (
    <div className="pt-2">
      <h4 className="text-base font-semibold text-[var(--text-primary)]">{title}</h4>
      {subtitle ? <p className="text-sm text-[var(--text-secondary)] mt-0.5">{subtitle}</p> : null}
    </div>
  )
}

function TriStateRadio({
  name,
  value,
  onChange,
  disabled,
}: {
  name: string
  value: TriState
  onChange: (v: TriState) => void
  disabled: boolean
}) {
  const { mapLabel } = useUpsellContext()
  const options: { value: TriState; label: string }[] = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'unknown', label: mapLabel("Don't know") },
  ]

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <label
          key={opt.value}
          className={`px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
            value === opt.value
              ? opt.value === 'unknown'
                ? 'border-amber-500 bg-amber-500/15 text-amber-400 font-medium'
                : 'border-primary bg-primary/10 text-[var(--text-primary)]'
              : 'border-[var(--border-color)] text-[var(--text-secondary)] hover:border-gray-600'
          } ${disabled ? (value === opt.value ? 'cursor-not-allowed' : 'opacity-40 cursor-not-allowed') : ''}`}
        >
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            disabled={disabled}
            className="hidden"
          />
          {opt.value === 'unknown' && value === 'unknown' ? `⚠ ${opt.label}` : opt.label}
        </label>
      ))}
    </div>
  )
}

const normalizeTriState = (value: unknown): TriState => {
  if (value === 'yes' || value === 'no' || value === 'unknown') return value
  if (value === true) return 'yes'
  if (value === false) return 'no'
  return 'unknown'
}

const normalizeCreditHeaderCheckMode = (value: unknown): TriState => {
  if (value === 'on_behalf') return 'yes'
  return normalizeTriState(value)
}

const JURISDICTIONS = [
  "Don't know",
  'Australia',
  'New Zealand',
  'United States',
  'United Kingdom',
  'Canada',
  'European Union Countries',
  'Asia-Pacific (specify in notes)',
  'Latin America (specify in notes)',
  'Middle East & Africa (specify in notes)',
  'Other (specify in notes)',
  'Unknown / To Be Determined',
]

const INTERNATIONAL_COUNTRIES_NONE = 'None'
const INTERNATIONAL_COUNTRIES_DONT_KNOW = "Don't know"

interface KycConfigTabProps {
  onSwitchTab?: (tab: string) => void
}

export function KycConfigTab({ onSwitchTab }: KycConfigTabProps) {
  const {
    scope,
    updateKycConfig,
    addKycRuleset,
    updateKycRuleset,
    removeKycRuleset,
    toggleKycDocumentAu,
    addKycInternationalCountry,
    removeKycInternationalCountry,
  } = useScopeStore()
  const { kycConfig } = scope
  const resellerImported = useHasResellerParent()
  const disabled = scope.finalized || resellerImported
  const isV1 = scope.generalInfo.platformVersion === 'v1'

  // State for searchable data source dropdown
  const [dataSourceSearch, setDataSourceSearch] = useState('')
  const [showDataSourceDropdown, setShowDataSourceDropdown] = useState(false)
  const dataSourceDropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dataSourceDropdownRef.current && !dataSourceDropdownRef.current.contains(event.target as Node)) {
        setShowDataSourceDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Sync credit header selections with selectedDataSources
  useEffect(() => {
    const equifaxEnabled = normalizeTriState(kycConfig.creditBureauSources?.equifaxCreditHeader) === 'yes'
    const experianEnabled = normalizeTriState(kycConfig.creditBureauSources?.experianCreditHeader) === 'yes'

    const equifaxSourceId: WorkflowCascadeSource = 'equifax_consumer_bureau_credit_header'
    const experianSourceId: WorkflowCascadeSource = 'experian_bureau_credit_header'

    const currentSources = kycConfig.selectedDataSources || []
    let needsUpdate = false
    let updatedSources = [...currentSources]

    // Add Equifax if enabled and not already in list
    if (equifaxEnabled && !currentSources.some(s => s.sourceId === equifaxSourceId)) {
      updatedSources.push({
        sourceId: equifaxSourceId,
        via: 'frankieone',
        directNote: ''
      })
      needsUpdate = true
    }

    // Add Experian if enabled and not already in list
    if (experianEnabled && !currentSources.some(s => s.sourceId === experianSourceId)) {
      updatedSources.push({
        sourceId: experianSourceId,
        via: 'frankieone',
        directNote: ''
      })
      needsUpdate = true
    }

    // Remove Equifax if disabled
    if (!equifaxEnabled && currentSources.some(s => s.sourceId === equifaxSourceId)) {
      updatedSources = updatedSources.filter(s => s.sourceId !== equifaxSourceId)
      needsUpdate = true
    }

    // Remove Experian if disabled
    if (!experianEnabled && currentSources.some(s => s.sourceId === experianSourceId)) {
      updatedSources = updatedSources.filter(s => s.sourceId !== experianSourceId)
      needsUpdate = true
    }

    if (needsUpdate) {
      updateKycConfig('selectedDataSources', updatedSources)
    }
  }, [kycConfig.creditBureauSources?.equifaxCreditHeader, kycConfig.creditBureauSources?.experianCreditHeader, kycConfig.selectedDataSources, updateKycConfig])

  // Sync NZ-specific fields with International KYC Countries selection.
  // The "Accept New Zealand Customers" section has been absorbed into International KYC —
  // selecting/removing New Zealand in International KYC Countries drives acceptNzCustomers
  // and documentTypesNz, keeping hour calculations and exports consistent.
  useEffect(() => {
    const nzSelected = (kycConfig.internationalCountries || []).includes('New Zealand')
    const currentAcceptNz = normalizeTriState(kycConfig.acceptNzCustomers)
    const targetAcceptNz: TriState = nzSelected ? 'yes' : 'no'
    if (currentAcceptNz !== targetAcceptNz) {
      updateKycConfig('acceptNzCustomers', targetAcceptNz)
    }

    // Mirror NZ document type checkboxes from international acceptance config
    if (nzSelected) {
      const nzIntl = kycConfig.internationalDocumentAcceptance?.['New Zealand']
      const desired: string[] = []
      if (nzIntl?.passport) desired.push('passport')
      if (nzIntl?.driversLicense) desired.push('drivers_license')
      const current = kycConfig.documentTypesNz || []
      const sameLength = current.length === desired.length
      const sameItems = sameLength && desired.every((d) => current.includes(d))
      if (!sameItems) updateKycConfig('documentTypesNz', desired)
    } else if ((kycConfig.documentTypesNz || []).length > 0) {
      updateKycConfig('documentTypesNz', [])
    }
  }, [
    kycConfig.internationalCountries,
    kycConfig.internationalDocumentAcceptance,
    kycConfig.acceptNzCustomers,
    kycConfig.documentTypesNz,
    updateKycConfig,
  ])

  const tabHeaderGroup = getGroupById('kyc_tab_header')

  const coreSetupSection = getSectionById('kyc_core_setup')
  const documentsRegionsSection = getSectionById('kyc_documents_regions')
  const jurisdictionsSourcesSection = getSectionById('kyc_jurisdictions_sources')

  const auDocTypesGroup = getGroupById('kyc_au_document_types')
  const additionalAuDocsGroup = getGroupById('kyc_additional_au_id_documents_group')
  const otherAuIdTypesToggleGroup = getGroupById('kyc_other_au_id_types_toggle_group')
  const otherAuDocumentTypesTextGroup = getGroupById('kyc_other_au_document_types_text_group')
  const creditHeaderCheckGroup = getGroupById('kyc_credit_header_check')
  const internationalSelectedCountriesGroup = getGroupById('kyc_international_selected_countries_group')
  const internationalAcceptedIdTypesGroup = getGroupById('kyc_international_accepted_id_types_group')
  const internationalSearchGroup = getGroupById('kyc_international_search_group')
  const primaryJurisdictionsGroup = getGroupById('kyc_primary_jurisdictions_group')
  const creditBureauSourcesGroup = getGroupById('kyc_credit_bureau_sources_group')
  const internationalDataSourcesGroup = getGroupById('kyc_international_data_sources_group')
  const foreignPassportVerificationField = getFieldById('kyc_foreign_passport_verification')
  const internationalKycCountriesField = getFieldById('kyc_international_kyc_countries')
  const auOtherDocumentTypesTextField = getFieldById('kyc_au_other_document_types_text')
  const primaryCustomerJurisdictionsField = getFieldById('kyc_primary_customer_jurisdictions')
  const geographicCoverageNotesField = getFieldById('kyc_geographic_coverage_notes')
  const creditEquifaxField = getFieldById('kyc_credit_bureau_equifax_credit_header')
  const creditExperianField = getFieldById('kyc_credit_bureau_experian_credit_header')
  const intlGdcField = getFieldById('kyc_international_data_source_gdc')
  const intlCentrixField = getFieldById('kyc_international_data_source_centrix')
  const intlEquifaxNzField = getFieldById('kyc_international_data_source_equifax_nz')
  const intlAsiaVerifyField = getFieldById('kyc_international_data_source_asia_verify')
  const intlMonnaiField = getFieldById('kyc_international_data_source_monnai')
  const intlOtherRegionalProvidersField = getFieldById('kyc_international_data_source_other_regional_providers')
  const dataSourceNotesField = getFieldById('kyc_data_source_notes')
  const [showOtherAuDocs, setShowOtherAuDocs] = useState(false)
  const [internationalCountryQuery, setInternationalCountryQuery] = useState('')
  const [jurisdictionSearch, setJurisdictionSearch] = useState('')

  const kycRequiredValue = normalizeTriState(kycConfig.kycRequired)
  const reportingEntityValue = normalizeTriState(kycConfig.isReportingEntity)
  const isReportingEntity = reportingEntityValue === 'yes'
  const creditHeaderMode = normalizeCreditHeaderCheckMode(kycConfig.enableConsentSources)
  const creditHeaderEnabled = creditHeaderMode === 'yes'
  const includesAustralia = kycConfig.primaryCustomerJurisdictions?.includes('Australia') ?? false
  const hasInternationalJurisdiction = (kycConfig.primaryCustomerJurisdictions ?? []).some(
    (j) => !['Australia', 'New Zealand', 'Unknown / To Be Determined', "Don't know"].includes(j)
  )

  // Incomplete field highlighting (only when KYC is required, suppressed for upsells)
  const { incompleteClass, isUpsell, mapLabel } = useUpsellContext()
  const kycRequired = !isUpsell && kycRequiredValue === 'yes'
  const isRulesetsIncomplete = kycRequired && (!kycConfig.rulesets || kycConfig.rulesets.length === 0 || !kycConfig.rulesets.some(r => r.rulesetKey && r.rulesetKey !== 'unknown'))
  const hasMeaningfulJurisdiction = (kycConfig.primaryCustomerJurisdictions ?? []).some(
    (j) => !["Don't know", 'Unknown / To Be Determined'].includes(j)
  )
  const isJurisdictionsIncomplete = kycRequired && !hasMeaningfulJurisdiction
  const isAuDocTypesIncomplete = kycRequired && includesAustralia && kycConfig.documentTypesAu.length === 0
  const isCreditBureauIncomplete = kycRequired && isReportingEntity && creditHeaderEnabled && !Object.values(kycConfig.creditBureauSources ?? {}).some(v => v !== undefined && v !== 'unknown')
  const isIntlDataSourcesIncomplete = kycRequired && hasInternationalJurisdiction && !Object.values(kycConfig.internationalDataSources ?? {}).some(v => v !== undefined && v !== 'unknown')

  const getInternationalAcceptance = (country: string) => {
    const existing = kycConfig.internationalDocumentAcceptance?.[country]
    return (
      existing || {
        passport: false,
        driversLicense: false,
        otherEnabled: false,
        otherText: '',
      }
    )
  }

  const commonInternationalCountries = useMemo(() => {
    const preferred = [
      'United States',
      'United Kingdom',
      'Canada',
      'Singapore',
      'Hong Kong',
      'India',
      'Philippines',
      'Malaysia',
      'Indonesia',
      'Thailand',
      'Vietnam',
      'Japan',
    ]

    const fromCommon = COMMON_COUNTRIES.filter((c) => preferred.includes(c))
    const combined = Array.from(new Set([...preferred, ...fromCommon]))
    return combined.slice(0, 12)
  }, [])

  const allCountryNames = useMemo(() => {
    const fallback = [
      ...commonInternationalCountries,
      'Germany',
      'France',
      'Italy',
      'Spain',
      'Netherlands',
      'Ireland',
      'South Korea',
      'South Africa',
      'Brazil',
      'Mexico',
      'United Arab Emirates',
    ]

    try {
      const supportedValuesOf = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf
      if (!supportedValuesOf) return Array.from(new Set(fallback)).sort((a, b) => a.localeCompare(b))

      const regions = supportedValuesOf('region')
      const displayNames = new Intl.DisplayNames(['en'], { type: 'region' })
      const names = regions
        .map((code) => displayNames.of(code))
        .filter((n): n is string => Boolean(n))

      return Array.from(new Set([...names, ...fallback])).sort((a, b) => a.localeCompare(b))
    } catch {
      return Array.from(new Set(fallback)).sort((a, b) => a.localeCompare(b))
    }
  }, [commonInternationalCountries])

  const toggleInternationalCountry = (country: string) => {
    const current = kycConfig.internationalCountries || []

    if (country === INTERNATIONAL_COUNTRIES_NONE || country === INTERNATIONAL_COUNTRIES_DONT_KNOW) {
      if (current.includes(country)) {
        updateKycConfig('internationalCountries', [])
      } else {
        updateKycConfig('internationalCountries', [country])
      }
      return
    }

    const cleared = current.filter((c) => c !== INTERNATIONAL_COUNTRIES_NONE && c !== INTERNATIONAL_COUNTRIES_DONT_KNOW)
    if (cleared.includes(country)) removeKycInternationalCountry(country)
    else {
      if (cleared.length !== current.length) updateKycConfig('internationalCountries', cleared)
      addKycInternationalCountry(country)
    }
  }

  return (
    <div className="space-y-6">
      <ResellerInheritedBanner />
      {/* Section Header */}
      <div className="border-b border-[var(--border-color)] pb-4">
        <h3 className="text-lg font-medium text-[var(--text-primary)]">
          {tabHeaderGroup?.formTitle || 'KYC Configuration'}
        </h3>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          {tabHeaderGroup?.formDescription || 'Identity verification settings and document types'}
        </p>
      </div>

      {/* KYC Required Field */}
      <div className="border border-[var(--border-color)] rounded-lg p-4 bg-[var(--bg-secondary)]/30">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-[var(--text-primary)]">
              KYC Verification Required
              <RequiredMark />
              <Tooltip content="Select whether KYC verification is required for this customer. Selecting 'No' will hide all other KYC configuration fields.">
                <span className="ml-2 inline-flex align-middle">
                  <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                </span>
              </Tooltip>
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              <span className="text-primary font-bold mr-1">*</span>
              Does this customer require KYC identity verification?
            </p>
          </div>
          <TriStateRadio
            name="kyc-required"
            value={kycRequiredValue}
            onChange={(v) => updateKycConfig('kycRequired', v)}
            disabled={disabled}
          />
        </div>
      </div>

      {/* All existing KYC sections - only show if KYC is not explicitly set to "no" */}
      {kycRequiredValue !== 'no' && (
        <>
          <SectionHeader
            title={coreSetupSection?.formTitle || 'Core setup'}
            subtitle={coreSetupSection?.formDescription || 'High-level decisions required to configure KYC verification'}
          />

      {/* KYC Ruleset Builder - Multiple Rulesets */}
      <div className={isRulesetsIncomplete ? incompleteClass : ''}>
      <div className="border border-[var(--border-color)] rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-medium text-[var(--text-primary)]">
              KYC Rulesets
              <RequiredMark />
            </h4>
            <Tooltip
              content={isV1
                ? "Add one or more KYC rulesets. V1 uses simplified verification levels (1+1 or 2+2). Gov ID is configured as a separate check type in the workflow."
                : "Add one or more KYC rulesets. Each ruleset defines how many data sources should verify each attribute (0-3). Clients can have multiple rulesets for different scenarios."
              }
            >
              <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
            </Tooltip>
          </div>
          <button
            className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => {
              const newRulesetKey = 'unknown'
              addKycRuleset(newRulesetKey)
            }}
            disabled={disabled}
          >
            <Plus size={16} />
            <span className="text-sm">Add Ruleset</span>
          </button>
        </div>

        <p className="text-xs text-[var(--text-muted)]">
          <span className="text-primary font-bold mr-1">*</span>
          {isV1
            ? 'Select a V1 verification level (1+1 or 2+2). Gov ID is configured as a separate add-on check type in the workflow, not as part of the ruleset.'
            : 'Select predefined rulesets or build custom ones. Each ruleset defines how many independent data sources must verify each attribute. You can add multiple rulesets for different verification scenarios.'
          }
        </p>

        {/* List of Rulesets */}
        {kycConfig.rulesets.length === 0 ? (
          <div className="text-center py-8 text-[var(--text-muted)] text-sm">
            No rulesets configured. Click &quot;Add Ruleset&quot; to get started.
          </div>
        ) : (
          <div className="space-y-4">
            {kycConfig.rulesets.map((ruleset, index) => {
              const v2Ruleset = KYC_RULESETS.find(r => r.key === ruleset.rulesetKey)
              const v1Ruleset = V1_KYC_RULESETS.find(r => r.key === ruleset.rulesetKey)
              const isCustom = ruleset.rulesetKey === 'custom'

              return (
                <div key={ruleset.id} className="border border-[var(--border-color)] rounded-lg p-4 space-y-3">
                  {/* Ruleset Header */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-semibold text-[var(--text-muted)]">Ruleset #{index + 1}</span>
                        {!isV1 && v2Ruleset && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
                            {v2Ruleset.region}
                          </span>
                        )}
                        {!isV1 && v2Ruleset?.recommended && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-yellow-500/15 text-yellow-600 border border-yellow-500/30"
                            title="Recommended — best match rate for 2+2 verifications in Australia"
                          >
                            ★ Recommended
                          </span>
                        )}
                        {isV1 && v1Ruleset && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-500/10 text-amber-500">
                            V1
                          </span>
                        )}
                      </div>

                      {/* Ruleset Selection Dropdown */}
                      <select
                        className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                        value={ruleset.rulesetKey}
                        onChange={(e) => {
                          const newKey = e.target.value
                          const newCustomReqs = newKey === 'custom' ? {
                            dataSources: 0,
                            address: 0,
                            dateOfBirth: 0,
                            fullName: 0,
                            govId: 0,
                          } : undefined
                          updateKycRuleset(ruleset.id, newKey, newCustomReqs, ruleset.name)
                        }}
                        disabled={disabled}
                      >
                        <option value="unknown">Don&apos;t know</option>
                        {isV1 ? (
                          // Simplified V1 rulesets
                          <>
                            {V1_KYC_RULESETS.map(r => (
                              <option key={r.key} value={r.key}>{r.label}</option>
                            ))}
                          </>
                        ) : (
                          // Full V2 rulesets by region — prefix recommended with a star
                          <>
                            <optgroup label="Australian Rulesets">
                              {KYC_RULESETS.filter(r => r.region === 'AU').map(r => (
                                <option key={r.key} value={r.key}>
                                  {r.recommended ? '★ RECOMMENDED — ' : ''}{r.label}
                                </option>
                              ))}
                            </optgroup>
                            <optgroup label="New Zealand Rulesets">
                              {KYC_RULESETS.filter(r => r.region === 'NZ').map(r => (
                                <option key={r.key} value={r.key}>
                                  {r.recommended ? '★ RECOMMENDED — ' : ''}{r.label}
                                </option>
                              ))}
                            </optgroup>
                            <optgroup label="International Rulesets">
                              {KYC_RULESETS.filter(r => r.region === 'INTL').map(r => (
                                <option key={r.key} value={r.key}>
                                  {r.recommended ? '★ RECOMMENDED — ' : ''}{r.label}
                                </option>
                              ))}
                            </optgroup>
                            <option value="custom">Custom Ruleset</option>
                          </>
                        )}
                      </select>

                      {/* Optional Name */}
                      <input
                        type="text"
                        placeholder="Optional: Custom name for this ruleset"
                        className="w-full h-10 mt-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-primary disabled:opacity-50"
                        value={ruleset.name || ''}
                        onChange={(e) => updateKycRuleset(ruleset.id, ruleset.rulesetKey, ruleset.customRequirements, e.target.value)}
                        disabled={disabled}
                      />
                    </div>

                    <button
                      className="flex-shrink-0 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                      onClick={() => removeKycRuleset(ruleset.id)}
                      disabled={disabled}
                      title="Remove ruleset"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  {/* Display Requirements */}
                  {ruleset.rulesetKey && ruleset.rulesetKey !== 'unknown' && (
                    <div className="mt-3">
                      {isV1 && v1Ruleset ? (
                        // Simplified V1 ruleset display
                        <div className="bg-[var(--bg-tertiary)]/50 border border-[var(--border-color)] rounded p-3">
                          <p className="text-xs text-[var(--text-muted)]">{v1Ruleset.description}</p>
                          <p className="text-xs text-amber-500/60 mt-2">Gov ID is configured as a separate check type in the workflow, not as part of the ruleset.</p>
                        </div>
                      ) : isCustom ? (
                        // Custom Ruleset Builder (V2 only)
                        <div className="space-y-3">
                          <h6 className="text-xs font-semibold text-[var(--text-primary)]">Custom Configuration</h6>
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-[var(--bg-tertiary)] border-b border-[var(--border-color)]">
                                  <th className="text-left p-2 text-[var(--text-primary)] font-semibold">Attribute</th>
                                  <th className="text-center p-2 text-[var(--text-primary)] font-semibold">Sources (0-3)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(['dataSources', 'address', 'dateOfBirth', 'fullName', 'govId'] as const).map((field) => (
                                  <tr key={field} className="border-b border-[var(--border-color)]">
                                    <td className="p-2 text-[var(--text-secondary)]">
                                      {field === 'dataSources' && 'Total Data Sources'}
                                      {field === 'address' && 'Address'}
                                      {field === 'dateOfBirth' && 'Date of Birth'}
                                      {field === 'fullName' && 'Full Name'}
                                      {field === 'govId' && 'Government ID'}
                                    </td>
                                    <td className="p-2">
                                      <div className="flex justify-center gap-2">
                                        {[0, 1, 2, 3].map(num => (
                                          <label key={num} className="flex items-center gap-1 cursor-pointer">
                                            <input
                                              type="radio"
                                              name={`custom-${ruleset.id}-${field}`}
                                              value={num}
                                              checked={(ruleset.customRequirements?.[field] ?? 0) === num}
                                              onChange={() => {
                                                const current = ruleset.customRequirements || {
                                                  dataSources: 0,
                                                  address: 0,
                                                  dateOfBirth: 0,
                                                  fullName: 0,
                                                  govId: 0,
                                                }
                                                updateKycRuleset(ruleset.id, ruleset.rulesetKey, {
                                                  ...current,
                                                  [field]: num,
                                                }, ruleset.name)
                                              }}
                                              disabled={disabled}
                                              className="w-4 h-4"
                                            />
                                            <span className="text-xs text-[var(--text-secondary)]">{num}</span>
                                          </label>
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : v2Ruleset ? (
                        // Display selected predefined V2 ruleset
                        <div className="bg-[var(--bg-tertiary)]/50 border border-[var(--border-color)] rounded p-3">
                          <p className="text-xs text-[var(--text-muted)] mb-2">{v2Ruleset.description}</p>
                          <div className="grid grid-cols-4 gap-2">
                            <div className="text-center">
                              <div className="text-xs text-[var(--text-muted)] mb-1">Address</div>
                              <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-500/20 text-blue-600 dark:text-blue-400 font-semibold text-sm">
                                {v2Ruleset.requirements.address}
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-xs text-[var(--text-muted)] mb-1">DOB</div>
                              <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-500/20 text-green-600 dark:text-green-400 font-semibold text-sm">
                                {v2Ruleset.requirements.dateOfBirth}
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-xs text-[var(--text-muted)] mb-1">Name</div>
                              <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-purple-500/20 text-purple-600 dark:text-purple-400 font-semibold text-sm">
                                {v2Ruleset.requirements.fullName}
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-xs text-[var(--text-muted)] mb-1">Gov ID</div>
                              <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-500/20 text-orange-600 dark:text-orange-400 font-semibold text-sm">
                                {v2Ruleset.requirements.govId}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
      </div>

      <SectionHeader
        title={documentsRegionsSection?.formTitle || 'Documents & regions'}
        subtitle={
          documentsRegionsSection?.formDescription ||
          'Document types and customer geography to shape data sources and coverage'
        }
      />

      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
      {/* Australian Customers */}
      <div className={isAuDocTypesIncomplete ? incompleteClass : ''}>
      <div className="border border-[var(--border-color)] rounded-lg p-4">
        <h4 className="text-sm font-medium text-[var(--text-primary)] mb-3">
          {auDocTypesGroup?.formTitle || 'Australian Customers - Document Types'}
          {includesAustralia ? <RequiredMark /> : null}
          <Tooltip
            content={
              getGuidance('KYC', 'Australian document types') || auDocTypesGroup?.tooltip || ''
            }
          >
            <span className="ml-2 inline-flex align-middle">
              <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
            </span>
          </Tooltip>
        </h4>

        {/* Don't know option */}
        <div className="mb-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
              checked={kycConfig.documentTypesAu.includes('unknown')}
              onChange={() => {
                if (kycConfig.documentTypesAu.includes('unknown')) {
                  // Remove "unknown" - user is now selecting specific types
                  updateKycConfig('documentTypesAu', kycConfig.documentTypesAu.filter(id => id !== 'unknown'))
                } else {
                  // Set to "unknown" and clear all other selections
                  updateKycConfig('documentTypesAu', ['unknown'])
                  updateKycConfig('auOtherDocumentTypesEnabled', false)
                  updateKycConfig('auOtherDocumentTypesText', '')
                }
              }}
              disabled={disabled}
            />
            <span className="text-sm font-medium text-[var(--text-primary)]">{mapLabel("Don't know")}</span>
          </label>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {AU_DOCUMENT_TYPES.map((doc) => (
            <label key={doc.id} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
                checked={kycConfig.documentTypesAu.includes(doc.id)}
                onChange={() => {
                  // If "unknown" is selected, clear it first when selecting a specific type
                  if (kycConfig.documentTypesAu.includes('unknown')) {
                    updateKycConfig('documentTypesAu', [doc.id])
                  } else {
                    toggleKycDocumentAu(doc.id)
                  }
                }}
                disabled={disabled || kycConfig.documentTypesAu.includes('unknown')}
              />
              <span className="text-sm text-[var(--text-primary)]">{doc.label}</span>
            </label>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
              checked={Boolean(kycConfig.auOtherDocumentTypesEnabled)}
              onChange={(e) => {
                const enabled = e.target.checked
                // If "unknown" is selected, clear it first
                if (kycConfig.documentTypesAu.includes('unknown')) {
                  updateKycConfig('documentTypesAu', [])
                }
                updateKycConfig('auOtherDocumentTypesEnabled', enabled)
                if (!enabled && kycConfig.auOtherDocumentTypesText) updateKycConfig('auOtherDocumentTypesText', '')
              }}
              disabled={disabled || kycConfig.documentTypesAu.includes('unknown')}
            />
            <span className="text-sm text-[var(--text-primary)]">Other (specify)</span>
          </label>

          {kycConfig.auOtherDocumentTypesEnabled ? (
            <div className="mt-3">
              <label className="block text-sm text-[var(--text-secondary)] mb-2">
                {otherAuDocumentTypesTextGroup?.formTitle || 'Other Australian document types'}
              </label>
              <input
                type="text"
                className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                placeholder={
                  auOtherDocumentTypesTextField?.placeholder ||
                  'e.g., Student ID, Government employee ID'
                }
                value={kycConfig.auOtherDocumentTypesText}
                onChange={(e) => updateKycConfig('auOtherDocumentTypesText', e.target.value)}
                disabled={disabled}
              />
            </div>
          ) : null}
        </div>

        <div className="mt-4">
          <button
            type="button"
            className="flex items-center gap-2 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            onClick={() => setShowOtherAuDocs((v) => !v)}
            disabled={disabled}
          >
            {showOtherAuDocs ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {otherAuIdTypesToggleGroup?.formTitle || 'Other Australian ID types'}
          </button>

          {showOtherAuDocs ? (
            <div className="mt-3 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="text-sm font-medium text-[var(--text-primary)]">
                  {additionalAuDocsGroup?.formTitle || 'Additional Australian ID documents'}
                </div>
                <Tooltip
                  content={
                    getGuidance('KYC', 'Additional Australian ID documents') ||
                    additionalAuDocsGroup?.tooltip ||
                    ''
                  }
                >
                  <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                </Tooltip>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {AU_OTHER_DOCUMENT_TYPES.map((doc) => (
                  <label key={doc.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
                      checked={kycConfig.documentTypesAu.includes(doc.id)}
                      onChange={() => toggleKycDocumentAu(doc.id)}
                      disabled={disabled}
                    />
                    <span className="text-sm text-[var(--text-primary)]">{doc.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        {includesAustralia ? (
          <p className="text-xs text-[var(--text-muted)] mt-2">
            <span className="text-primary font-bold mr-1">*</span>
            {auDocTypesGroup?.helperText ||
              'Required for completion (when Australia is a primary jurisdiction). Select at least one.'}
          </p>
        ) : null}

        <div className="mt-3 bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 text-xs text-[var(--text-secondary)]">
          <strong className="text-[var(--text-primary)]">Note on document capture:</strong> Via the
          FrankieOne portal, documents can be attached to the entity and processed against these
          document types. However, capturing details by having the customer take a photo and
          extracting details via OCR is typically restricted to <strong>Passport</strong> and{' '}
          <strong>Driver's Licence</strong> only.
        </div>
      </div>
      </div>

      {/* Credit Header Check */}
      <div className="border border-[var(--border-color)] rounded-lg p-4">
        <div>
          <div className="text-sm font-medium text-[var(--text-primary)]">
            {creditHeaderCheckGroup?.formTitle}
            {creditHeaderCheckGroup?.tooltip && (
              <Tooltip content={creditHeaderCheckGroup.tooltip}>
                <span className="ml-2 inline-flex align-middle">
                  <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                </span>
              </Tooltip>
            )}
          </div>
          {creditHeaderCheckGroup?.formDescription && (
            <p className="text-xs text-[var(--text-muted)] mt-1">
              {creditHeaderCheckGroup.formDescription}
            </p>
          )}
          {!isReportingEntity && (
            <div className="flex items-start gap-2 mt-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <AlertTriangle size={16} className="text-amber-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-400">Reporting Entity Required</p>
                <p className="text-xs text-amber-300/80 mt-1">
                  Credit header checks are only available to reporting entities.{' '}
                  {onSwitchTab ? (
                    <button
                      type="button"
                      onClick={() => onSwitchTab('advanced')}
                      className="underline text-amber-400 hover:text-amber-300 font-medium"
                    >
                      Go to Advanced &rarr; KYC to set Reporting Entity
                    </button>
                  ) : (
                    'Set Reporting Entity to Yes in the Advanced tab (KYC section) to enable this option.'
                  )}
                </p>
              </div>
            </div>
          )}
        </div>

        {isReportingEntity && (
          <div className="flex flex-wrap gap-2 mt-3">
            {(
              [
                { value: 'unknown' as const, label: mapLabel("Don't know") },
                { value: 'no' as const, label: 'No' },
                { value: 'yes' as const, label: 'Yes' },
              ] as const
            ).map((opt) => (
              <label
                key={opt.value}
                className={`px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                  creditHeaderMode === opt.value
                    ? 'border-primary bg-primary/10 text-[var(--text-primary)]'
                    : 'border-[var(--border-color)] text-[var(--text-secondary)] hover:border-gray-600'
                } ${disabled ? (creditHeaderMode === opt.value ? 'cursor-not-allowed' : 'opacity-40 cursor-not-allowed') : ''}`}
              >
                <input
                  type="radio"
                  name="enable-consent-sources"
                  value={opt.value}
                  checked={creditHeaderMode === opt.value}
                  onChange={() => updateKycConfig('enableConsentSources', opt.value)}
                  disabled={disabled}
                  className="hidden"
                />
                {mapLabel(opt.label)}
              </label>
            ))}
          </div>
        )}

        {/* Credit Header Compliance Warning - Full Width */}
        {isReportingEntity && creditHeaderEnabled && (
          <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <div className="flex gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5 text-amber-600 dark:text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-2">
                  Important Compliance Requirements
                </h4>
                <div className="text-xs text-amber-800 dark:text-amber-300 space-y-2">
                  <p className="font-medium">
                    By using credit header data, the customer must comply with the following obligations:
                  </p>

                      <div className="space-y-2 ml-2">
                        <div>
                          <p className="font-semibold">1. Capture Customer Consent</p>
                          <p className="ml-4">
                            You must obtain explicit opt-in consent from customers about the verification process before conducting credit header checks.
                          </p>
                        </div>

                        <div>
                          <p className="font-semibold">2. Credit Header Fail Notification</p>
                          <p className="ml-4">
                            When a customer's information fails to match credit header records, you must send a written notification to the customer within a reasonable time. This notification must include:
                          </p>
                          <ul className="ml-8 list-disc space-y-1">
                            <li>A statement that the organization is unable to verify their identity</li>
                            <li>The name(s) of the credit reporting body(s) used (e.g., Equifax, Illion, Experian)</li>
                            <li>An offer of alternative means to verify their identity</li>
                          </ul>
                          <p className="ml-4 mt-1 italic">
                            Note: This notification is independent of the overall KYC result - even if identity verification passes overall, a failure notification must still be sent if credit header checks failed.
                          </p>
                        </div>

                        <div>
                          <p className="font-semibold">3. Information Retention</p>
                          <p className="ml-4">
                            You must comply with information retention requirements as specified in the AML/CTF Act.
                          </p>
                        </div>
                      </div>

                  <p className="mt-3 pt-2 border-t border-amber-200 dark:border-amber-700 italic">
                    Disclaimer: This information is not legal advice. Please consult with your legal team to ensure full compliance with all applicable regulations.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Foreign Passport Verification */}
      <div className="border border-[var(--border-color)] rounded-lg p-4">
        <h4 className="text-sm font-medium text-[var(--text-primary)] mb-1">
          {foreignPassportVerificationField?.formLabel || 'Foreign Passport Verification'}
          <Tooltip
            content={
              foreignPassportVerificationField?.tooltip ||
              getGuidance('KYC', 'Foreign passport verification') ||
              ''
            }
          >
            <span className="ml-2 inline-flex align-middle">
              <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
            </span>
          </Tooltip>
        </h4>
        {foreignPassportVerificationField?.formDescription && (
          <p className="text-xs text-[var(--text-muted)] mt-1 mb-3">
            {foreignPassportVerificationField.formDescription}
          </p>
        )}
        <select
          className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
          value={kycConfig.dvsForeignPassport}
          onChange={(e) => updateKycConfig('dvsForeignPassport', e.target.value as any)}
          disabled={disabled}
        >
          {(
            (foreignPassportVerificationField?.options || [
              { value: '', label: mapLabel("Don't know") },
              { value: 'dvs', label: 'DVS Check' },
              { value: 'vevo', label: 'VEVO Check' },
              { value: 'na', label: 'None / Not Applicable' },
            ]) as Array<{ value: string; label: string }>
          ).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {mapLabel(opt.label)}
            </option>
          ))}
        </select>
      </div>

      {/* International KYC */}
      <div className="border border-[var(--border-color)] rounded-lg p-4">
        <h4 className="text-sm font-medium text-[var(--text-primary)] mb-3">
          {internationalKycCountriesField?.formLabel || 'International KYC Countries'}
          <Tooltip
            content={
              getGuidance('KYC', 'International KYC countries') ||
              internationalKycCountriesField?.tooltip ||
              ''
            }
          >
            <span className="ml-2 inline-flex align-middle">
              <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
            </span>
          </Tooltip>
        </h4>

        <div className="mb-4 bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 text-xs text-[var(--text-secondary)] space-y-2">
          <p>
            <strong className="text-[var(--text-primary)]">When DVS works for foreigners:</strong>{' '}
            When a person enters Australia legally, their passport details are recorded in the DVS.
            You can therefore often verify foreigners physically in Australia via their passport
            using DVS.
          </p>
          <p>
            <strong className="text-[var(--text-primary)]">When DVS does not work:</strong> If they
            leave Australia or are not in Australia at the time of verification, they will not be
            found in the DVS — an international data source is required. Equally, verifying a
            foreign person living in Australia using their international driver's licence will not
            work, as the DVS does not capture these details. A separate international data source
            will be required.
          </p>
          <p>
            <strong className="text-[var(--text-primary)]">New Zealand:</strong> Select New Zealand
            below to enable NZ-specific verification (e.g. Centrix). This handles both NZ citizens
            in Australia on Special Category Visas (who may not appear in DVS) and customers
            located in New Zealand.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
              checked={(kycConfig.internationalCountries || []).includes(INTERNATIONAL_COUNTRIES_NONE)}
              onChange={() => toggleInternationalCountry(INTERNATIONAL_COUNTRIES_NONE)}
              disabled={disabled}
            />
            <span className="text-sm text-[var(--text-primary)]">None</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
              checked={(kycConfig.internationalCountries || []).includes('New Zealand')}
              onChange={() => toggleInternationalCountry('New Zealand')}
              disabled={
                disabled ||
                (kycConfig.internationalCountries || []).includes(INTERNATIONAL_COUNTRIES_NONE) ||
                (kycConfig.internationalCountries || []).includes(INTERNATIONAL_COUNTRIES_DONT_KNOW)
              }
            />
            <span className="text-sm text-[var(--text-primary)]">New Zealand</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
              checked={(kycConfig.internationalCountries || []).includes(INTERNATIONAL_COUNTRIES_DONT_KNOW)}
              onChange={() => toggleInternationalCountry(INTERNATIONAL_COUNTRIES_DONT_KNOW)}
              disabled={disabled}
            />
            <span className="text-sm text-[var(--text-primary)]">{mapLabel("Don't know")}</span>
          </label>
        </div>

        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <label className="block text-sm text-[var(--text-secondary)] mb-2">
              {internationalSearchGroup?.formTitle || 'Search and add other countries'}
            </label>
            <input
              type="text"
              list="international-country-options"
              className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
              placeholder="Type a country name..."
              value={internationalCountryQuery}
              onChange={(e) => setInternationalCountryQuery(e.target.value)}
              disabled={
                disabled ||
                (kycConfig.internationalCountries || []).includes(INTERNATIONAL_COUNTRIES_NONE) ||
                (kycConfig.internationalCountries || []).includes(INTERNATIONAL_COUNTRIES_DONT_KNOW)
              }
            />
            <datalist id="international-country-options">
              {allCountryNames.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>

          <div className="md:pt-7">
            <button
              type="button"
              className="h-12 px-4 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
              onClick={() => {
                const next = internationalCountryQuery.trim()
                if (!next) return
                const current = kycConfig.internationalCountries || []
                const cleared = current.filter(
                  (c) => c !== INTERNATIONAL_COUNTRIES_NONE && c !== INTERNATIONAL_COUNTRIES_DONT_KNOW
                )
                if (cleared.length !== current.length) updateKycConfig('internationalCountries', cleared)
                addKycInternationalCountry(next)
                setInternationalCountryQuery('')
              }}
              disabled={
                disabled ||
                !internationalCountryQuery.trim() ||
                (kycConfig.internationalCountries || []).includes(INTERNATIONAL_COUNTRIES_NONE) ||
                (kycConfig.internationalCountries || []).includes(INTERNATIONAL_COUNTRIES_DONT_KNOW)
              }
            >
              Add
            </button>
          </div>
        </div>

        {(kycConfig.internationalCountries || []).length > 0 ? (
          <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
            <div className="text-sm font-medium text-[var(--text-primary)] mb-2">
              {internationalSelectedCountriesGroup?.formTitle || 'Selected countries'}
            </div>
            <div className="flex flex-wrap gap-2">
              {(kycConfig.internationalCountries || [])
                .slice()
                .sort((a, b) => a.localeCompare(b))
                .map((country) => (
                  <button
                    key={country}
                    type="button"
                    className="px-3 py-1.5 bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] rounded-lg text-sm hover:bg-[var(--bg-tertiary)] transition-colors disabled:opacity-50"
                    onClick={() => removeKycInternationalCountry(country)}
                    disabled={disabled}
                    title="Remove"
                  >
                    {country}
                  </button>
                ))}
            </div>
          </div>
        ) : null}

        {(() => {
          const selected = (kycConfig.internationalCountries || []).filter(
            (c) => c !== INTERNATIONAL_COUNTRIES_NONE && c !== INTERNATIONAL_COUNTRIES_DONT_KNOW
          )
          if (selected.length === 0) return null

          const sorted = selected.slice().sort((a, b) => a.localeCompare(b))

          return (
            <div className="mt-4 pt-4 border-t border-[var(--border-color)] space-y-4">
              <div className="text-sm font-medium text-[var(--text-primary)]">
                {internationalAcceptedIdTypesGroup?.formTitle ||
                  'International Customers - Accepted ID Types'}
              </div>
              {sorted.map((country) => {
                const cfg = getInternationalAcceptance(country)
                const updateCountry = (patch: Partial<typeof cfg>) => {
                  updateKycConfig('internationalDocumentAcceptance', {
                    ...(kycConfig.internationalDocumentAcceptance || {}),
                    [country]: {
                      ...cfg,
                      ...patch,
                    },
                  })
                }

                return (
                  <div key={country} className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
                    <div className="text-sm font-medium text-[var(--text-primary)] mb-3">{country}</div>

                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
                          checked={cfg.passport}
                          onChange={(e) => updateCountry({ passport: e.target.checked })}
                          disabled={disabled}
                        />
                        <span className="text-sm text-[var(--text-primary)]">Passport</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
                          checked={cfg.driversLicense}
                          onChange={(e) => updateCountry({ driversLicense: e.target.checked })}
                          disabled={disabled}
                        />
                        <span className="text-sm text-[var(--text-primary)]">Driver's license</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
                          checked={cfg.otherEnabled}
                          onChange={(e) => {
                            const enabled = e.target.checked
                            updateCountry({ otherEnabled: enabled, otherText: enabled ? cfg.otherText : '' })
                          }}
                          disabled={disabled}
                        />
                        <span className="text-sm text-[var(--text-primary)]">Other</span>
                      </label>
                    </div>

                    {cfg.otherEnabled ? (
                      <div className="mt-3">
                        <label className="block text-sm text-[var(--text-secondary)] mb-2">Other ID types (comma separated)</label>
                        <input
                          type="text"
                          className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                          placeholder="e.g., National ID, Residence permit"
                          value={cfg.otherText}
                          onChange={(e) => updateCountry({ otherText: e.target.value })}
                          disabled={disabled}
                        />
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          )
        })()}

        <p className="text-xs text-[var(--text-muted)] mt-2">
          {internationalKycCountriesField?.formDescription ||
            'Countries where international KYC verification is required'}
        </p>
      </div>
      </div>

      <SectionHeader
        title={jurisdictionsSourcesSection?.formTitle || 'Jurisdictions & data sources'}
        subtitle={
          jurisdictionsSourcesSection?.formDescription ||
          'Customer jurisdictions and selected data sources to support verification.'
        }
      />

      <div className="ml-3 pl-4 border-l-2 border-[var(--border-color)] space-y-4">
      <div className={isJurisdictionsIncomplete ? incompleteClass : ''}>
      <div className="border border-[var(--border-color)] rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium text-[var(--text-primary)]">
            {primaryJurisdictionsGroup?.formTitle ||
              primaryCustomerJurisdictionsField?.formLabel ||
              'Primary Customer Jurisdictions'}
            <RequiredMark />
          </h4>
          <Tooltip
            content={
              getGuidance('KYC', 'Primary customer jurisdictions') ||
              primaryJurisdictionsGroup?.tooltip ||
              primaryCustomerJurisdictionsField?.tooltip ||
              ''
            }
          >
            <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
          </Tooltip>
        </div>
        <p className="text-xs text-[var(--text-muted)]">
          <span className="text-primary font-bold mr-1">*</span>
          {primaryJurisdictionsGroup?.helperText ||
            'Required for completion. Select at least one (or keep “Don\'t know”).'}
        </p>
        {/* Quick-select options */}
        <div className="flex flex-wrap gap-3">
          {["Don't know", 'Australia'].map((j) => (
            <label key={j} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
                checked={(kycConfig.primaryCustomerJurisdictions || []).includes(j)}
                onChange={() => {
                  const current = kycConfig.primaryCustomerJurisdictions || []
                  let next = current.includes(j) ? current.filter((x) => x !== j) : [...current, j]
                  if (j === "Don't know") {
                    next = current.includes(j) ? [] : ["Don't know"]
                  } else {
                    next = next.filter((x) => x !== "Don't know")
                  }
                  updateKycConfig('primaryCustomerJurisdictions', next)
                }}
                disabled={disabled}
              />
              <span className="text-sm text-[var(--text-primary)]">{j === "Don't know" ? mapLabel("Don't know") : j}</span>
            </label>
          ))}
        </div>

        {/* Searchable jurisdiction selector */}
        <div className="space-y-2">
          <input
            type="text"
            className="w-full h-10 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-primary disabled:opacity-50"
            placeholder="Search for other jurisdictions..."
            value={jurisdictionSearch}
            onChange={(e) => setJurisdictionSearch(e.target.value)}
            disabled={disabled}
          />
          {jurisdictionSearch.trim() && (
            <div className="border border-[var(--border-color)] rounded-lg bg-[var(--bg-secondary)] max-h-48 overflow-y-auto">
              {JURISDICTIONS.filter(
                (j) =>
                  j !== "Don't know" &&
                  j !== 'Australia' &&
                  j.toLowerCase().includes(jurisdictionSearch.toLowerCase())
              ).length === 0 ? (
                <div className="px-4 py-3 text-sm text-[var(--text-muted)]">No matching jurisdictions</div>
              ) : (
                JURISDICTIONS.filter(
                  (j) =>
                    j !== "Don't know" &&
                    j !== 'Australia' &&
                    j.toLowerCase().includes(jurisdictionSearch.toLowerCase())
                ).map((j) => (
                  <label
                    key={j}
                    className="flex items-center gap-2 px-4 py-2 cursor-pointer hover:bg-[var(--bg-tertiary)] transition-colors"
                  >
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-secondary)] text-primary focus:ring-primary"
                      checked={(kycConfig.primaryCustomerJurisdictions || []).includes(j)}
                      onChange={() => {
                        const current = kycConfig.primaryCustomerJurisdictions || []
                        let next = current.includes(j) ? current.filter((x) => x !== j) : [...current, j]
                        next = next.filter((x) => x !== "Don't know")
                        updateKycConfig('primaryCustomerJurisdictions', next)
                      }}
                      disabled={disabled}
                    />
                    <span className="text-sm text-[var(--text-primary)]">{j}</span>
                  </label>
                ))
              )}
            </div>
          )}
        </div>

        {/* Selected jurisdictions (other than Don't know / Australia) */}
        {(kycConfig.primaryCustomerJurisdictions || []).filter(
          (j) => j !== "Don't know" && j !== 'Australia'
        ).length > 0 && (
          <div className="flex flex-wrap gap-2">
            {(kycConfig.primaryCustomerJurisdictions || [])
              .filter((j) => j !== "Don't know" && j !== 'Australia')
              .map((j) => (
                <span
                  key={j}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20"
                >
                  {j}
                  <button
                    type="button"
                    className="hover:text-red-500 transition-colors disabled:opacity-50"
                    onClick={() => {
                      const current = kycConfig.primaryCustomerJurisdictions || []
                      updateKycConfig('primaryCustomerJurisdictions', current.filter((x) => x !== j))
                    }}
                    disabled={disabled}
                  >
                    ×
                  </button>
                </span>
              ))}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
            {geographicCoverageNotesField?.formLabel || 'Geographic Coverage Notes'}
          </label>
          <textarea
            className="w-full h-20 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-none"
            placeholder={
              geographicCoverageNotesField?.placeholder ||
              'e.g., Primarily Australian customers but expect 10% from NZ and UK'
            }
            value={kycConfig.geographicCoverageNotes}
            onChange={(e) => updateKycConfig('geographicCoverageNotes', e.target.value)}
            disabled={disabled}
          />
        </div>

        {/* Data Sources Section */}
        <div className="mt-6 space-y-4">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-medium text-[var(--text-primary)]">Data Sources</h4>
            <Tooltip content="Select which data sources will be used for KYC verification, and specify whether they'll be accessed via FrankieOne or through a direct connection.">
              <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
            </Tooltip>
          </div>
          <p className="text-xs text-[var(--text-muted)]">
            Add data sources from the dropdown below. These mirror the cascade options available in the workflow builder.
          </p>

          {/* Searchable dropdown to add data sources */}
          <div>
            <label className="block text-sm text-[var(--text-secondary)] mb-2">Add Data Source</label>
            <div className="relative" ref={dataSourceDropdownRef}>
              <input
                type="text"
                className="w-full h-12 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50"
                placeholder="Type to search data sources..."
                value={dataSourceSearch}
                onChange={(e) => {
                  setDataSourceSearch(e.target.value)
                  setShowDataSourceDropdown(true)
                }}
                onFocus={() => setShowDataSourceDropdown(true)}
                disabled={disabled}
              />
              {showDataSourceDropdown && dataSourceSearch && !disabled && (
                <div className="absolute z-10 w-full mt-1 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {WORKFLOW_CASCADE_SOURCES.filter(
                    (source) =>
                      !(kycConfig.selectedDataSources || []).some((s) => s.sourceId === source.id) &&
                      (source.label.toLowerCase().includes(dataSourceSearch.toLowerCase()) ||
                        source.details.toLowerCase().includes(dataSourceSearch.toLowerCase()))
                  ).length === 0 ? (
                    <div className="px-4 py-3 text-sm text-[var(--text-secondary)]">No matching data sources found</div>
                  ) : (
                    WORKFLOW_CASCADE_SOURCES.filter(
                      (source) =>
                        !(kycConfig.selectedDataSources || []).some((s) => s.sourceId === source.id) &&
                        (source.label.toLowerCase().includes(dataSourceSearch.toLowerCase()) ||
                          source.details.toLowerCase().includes(dataSourceSearch.toLowerCase()))
                    ).map((source) => {
                      const isCreditHeaderLocked = Boolean(source.creditHeader) && !isReportingEntity
                      return (
                        <button
                          key={source.id}
                          type="button"
                          className={`w-full px-4 py-3 text-left border-b border-[var(--border-color)] last:border-b-0 ${isCreditHeaderLocked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[var(--bg-tertiary)]'}`}
                          disabled={isCreditHeaderLocked}
                          onClick={() => {
                            if (isCreditHeaderLocked) return
                            const current = kycConfig.selectedDataSources || []
                            updateKycConfig('selectedDataSources', [
                              ...current,
                              { sourceId: source.id as WorkflowCascadeSource, via: 'frankieone', directNote: '' },
                            ])
                            // Auto-enable the credit bureau toggle + credit header check when adding a credit header source
                            if (source.id === 'equifax_consumer_bureau_credit_header') {
                              updateKycConfig('enableConsentSources', 'yes')
                              updateKycConfig('creditBureauSources', { ...kycConfig.creditBureauSources, equifaxCreditHeader: 'yes' })
                            } else if (source.id === 'experian_bureau_credit_header') {
                              updateKycConfig('enableConsentSources', 'yes')
                              updateKycConfig('creditBureauSources', { ...kycConfig.creditBureauSources, experianCreditHeader: 'yes' })
                            }
                            setDataSourceSearch('')
                            setShowDataSourceDropdown(false)
                          }}
                        >
                          <div className="text-sm font-medium text-[var(--text-primary)]">
                            {source.label}
                            {source.region === 'international' && (
                              <span className="ml-2 text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded">
                                International
                              </span>
                            )}
                            {source.creditHeader && (
                              <span className="ml-2 text-xs px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded">
                                Credit Header
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-[var(--text-muted)] mt-0.5">{source.details}</div>
                          {isCreditHeaderLocked && (
                            <div className="text-xs text-amber-400 mt-1">Requires Reporting Entity — go to Advanced &rarr; KYC to enable</div>
                          )}
                        </button>
                      )
                    })
                  )}
                </div>
              )}
            </div>
          </div>

          {/* List of selected data sources */}
          {(kycConfig.selectedDataSources || []).length > 0 && (
            <div className="space-y-3">
              <div className="text-sm font-medium text-[var(--text-primary)]">
                Selected Data Sources ({(kycConfig.selectedDataSources || []).length})
              </div>
              {(kycConfig.selectedDataSources || []).map((selectedSource) => {
                const source = WORKFLOW_CASCADE_SOURCES.find((s) => s.id === selectedSource.sourceId)
                if (!source) return null

                const notFrankieOne = selectedSource.via === 'direct'

                return (
                  <div
                    key={selectedSource.sourceId}
                    className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-3 space-y-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-[var(--text-primary)]">
                          {source.label}
                          {source.region === 'international' && (
                            <span className="ml-2 text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded">
                              International
                            </span>
                          )}
                          {source.creditHeader && (
                            <span className="ml-2 text-xs px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded">
                              Credit Header
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-[var(--text-muted)] mt-0.5">{source.details}</div>
                      </div>
                      <button
                        type="button"
                        className="text-red-400 hover:text-red-300 p-1"
                        onClick={() => {
                          const current = kycConfig.selectedDataSources || []
                          updateKycConfig(
                            'selectedDataSources',
                            current.filter((s) => s.sourceId !== selectedSource.sourceId)
                          )
                        }}
                        disabled={disabled}
                        title="Remove data source"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    {/* Checkbox for "Not through FrankieOne" */}
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-[var(--border-color)] bg-[var(--bg-tertiary)] text-primary focus:ring-primary"
                          checked={notFrankieOne}
                          onChange={(e) => {
                            const current = kycConfig.selectedDataSources || []
                            updateKycConfig(
                              'selectedDataSources',
                              current.map((s) =>
                                s.sourceId === selectedSource.sourceId
                                  ? { ...s, via: e.target.checked ? 'direct' : 'frankieone', directNote: e.target.checked ? s.directNote : '' }
                                  : s
                              )
                            )
                          }}
                          disabled={disabled}
                        />
                        <span className="text-sm text-[var(--text-primary)]">
                          Not through FrankieOne (direct connection)
                        </span>
                      </label>

                      {/* Direct connection notes - shown when checkbox is checked */}
                      {notFrankieOne && (
                        <div>
                          <label className="block text-xs text-[var(--text-secondary)] mb-1">
                            Direct connection details
                          </label>
                          <textarea
                            className="w-full h-20 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-none"
                            placeholder="e.g., Client has existing contract, using their API credentials, special integration requirements..."
                            value={selectedSource.directNote}
                            onChange={(e) => {
                              const current = kycConfig.selectedDataSources || []
                              updateKycConfig(
                                'selectedDataSources',
                                current.map((s) =>
                                  s.sourceId === selectedSource.sourceId ? { ...s, directNote: e.target.value } : s
                                )
                              )
                            }}
                            disabled={disabled}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
      </div>

      {isReportingEntity && creditHeaderEnabled && (
        <div className={isCreditBureauIncomplete ? incompleteClass : ''}>
        <div className="border border-[var(--border-color)] rounded-lg p-4 space-y-4">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-medium text-[var(--text-primary)]">
              {creditBureauSourcesGroup?.formTitle || 'Credit Bureau Data Sources'}
              <RequiredMark />
            </h4>
            <Tooltip
              content={
                getGuidance('KYC', 'Credit Bureau Data Sources') ||
                creditBureauSourcesGroup?.tooltip ||
                ''
              }
            >
              <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
            </Tooltip>
          </div>
          <p className="text-xs text-[var(--text-muted)]">
            <span className="text-primary font-bold mr-1">*</span>
            {creditBureauSourcesGroup?.helperText ||
              'Required for completion (when Reporting Entity = Yes). These can be No, but shouldn’t be left Unknown.'}
          </p>
          {(
            [
              {
                key: 'equifaxCreditHeader',
                label: creditEquifaxField?.formLabel || 'Equifax Credit Header',
                tip: creditEquifaxField?.tooltip || '',
              },
              {
                key: 'experianCreditHeader',
                label: creditExperianField?.formLabel || 'Experian Credit Header',
                tip: creditExperianField?.tooltip || '',
              },
            ] as const
          ).map((item) => (
            <div key={item.key} className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-sm font-medium text-[var(--text-primary)]">{item.label}</div>
                <Tooltip content={getGuidance('KYC', item.label) || item.tip}>
                  <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                </Tooltip>
              </div>
              <TriStateRadio
                name={`credit-${item.key}`}
                value={normalizeTriState(kycConfig.creditBureauSources?.[item.key])}
                onChange={(v) =>
                  updateKycConfig('creditBureauSources', {
                    ...kycConfig.creditBureauSources,
                    [item.key]: v,
                  })
                }
                disabled={disabled}
              />
            </div>
          ))}
        </div>
        </div>
      )}

      {hasInternationalJurisdiction && (
        <div className={isIntlDataSourcesIncomplete ? incompleteClass : ''}>
        <div className="border border-[var(--border-color)] rounded-lg p-4 space-y-4">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-medium text-[var(--text-primary)]">
              {internationalDataSourcesGroup?.formTitle || 'International Data Sources'}
              <RequiredMark />
            </h4>
            <Tooltip
              content={
                getGuidance('KYC', 'International Data Sources') ||
                internationalDataSourcesGroup?.tooltip ||
                ''
              }
            >
              <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
            </Tooltip>
          </div>
          <p className="text-xs text-[var(--text-muted)]">
            <span className="text-primary font-bold mr-1">*</span>
            {internationalDataSourcesGroup?.helperText ||
              'Required for completion (when international jurisdictions are selected). These can be No, but shouldn’t be left Unknown.'}
          </p>
          {(
            [
              { key: 'gdc', label: intlGdcField?.formLabel || 'GDC (Global Data Consortium) - 40+ countries', tip: intlGdcField?.tooltip || '' },
              { key: 'centrix', label: intlCentrixField?.formLabel || 'Centrix (New Zealand)', tip: intlCentrixField?.tooltip || '' },
              { key: 'equifaxNz', label: intlEquifaxNzField?.formLabel || 'Equifax NZ (New Zealand)', tip: intlEquifaxNzField?.tooltip || '' },
              { key: 'asiaVerify', label: intlAsiaVerifyField?.formLabel || 'AsiaVerify (China/Asia)', tip: intlAsiaVerifyField?.tooltip || '' },
              { key: 'monnai', label: intlMonnaiField?.formLabel || 'Monnai (India)', tip: intlMonnaiField?.tooltip || '' },
              { key: 'otherRegionalProviders', label: intlOtherRegionalProvidersField?.formLabel || 'Other regional providers (specify in notes)', tip: intlOtherRegionalProvidersField?.tooltip || '' },
            ] as const
          ).map((item) => (
            <div key={item.key} className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-sm font-medium text-[var(--text-primary)]">{item.label}</div>
                <Tooltip content={item.tip}>
                  <HelpCircle size={14} className="text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
                </Tooltip>
              </div>
              <TriStateRadio
                name={`intl-${item.key}`}
                value={normalizeTriState(kycConfig.internationalDataSources?.[item.key])}
                onChange={(v) =>
                  updateKycConfig('internationalDataSources', {
                    ...kycConfig.internationalDataSources,
                    [item.key]: v,
                  })
                }
                disabled={disabled}
              />
            </div>
          ))}

          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
              {dataSourceNotesField?.formLabel || 'Data Source Selection Notes'}
            </label>
            <textarea
              className="w-full h-20 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-primary disabled:opacity-50 resize-none"
              placeholder={
                dataSourceNotesField?.placeholder ||
                'e.g., Prefer DVS for government IDs, cascade to Equifax if DVS fails'
              }
              value={kycConfig.dataSourceNotes}
              onChange={(e) => updateKycConfig('dataSourceNotes', e.target.value)}
              disabled={disabled}
            />
          </div>
        </div>
        </div>
      )}
      </div>

        </>
      )}
    </div>
  )
}
